import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  PANCHANGA_FALLBACK,
  PANCHANGA_LOC_KEY,
  parseLocFromSearchParams,
  loadCachedLocation,
  cacheLocation,
  browserTzOffsetMinutes,
  reverseGeocodeLabel,
} from "../lib/panchanga-location";
import { getApiBaseUrl } from "@/lib/api-base";

const API = getApiBaseUrl();

/**
 * Strict location priority: query → profile → geolocation → cached → fallback.
 */
export function usePanchangaLocation(token, user) {
  const [searchParams] = useSearchParams();
  const [loc, setLoc] = useState(null);
  const [meta, setMeta] = useState({ source: null, city: null, country: null, loading: true });
  const [message, setMessage] = useState("");
  const geoAttempted = useRef(false);

  const applyLoc = useCallback((nextLoc, nextMeta) => {
    setLoc({
      lat: nextLoc.lat,
      lon: nextLoc.lon,
      tz_offset_minutes: nextLoc.tz_offset_minutes,
      city: nextLoc.city,
      country: nextLoc.country,
    });
    setMeta({
      source: nextMeta.source,
      city: nextMeta.city ?? nextLoc.city ?? null,
      country: nextMeta.country ?? nextLoc.country ?? null,
      loading: false,
    });
    if (nextMeta.source === "auto" || nextMeta.source === "manual") {
      cacheLocation({ ...nextLoc, city: nextMeta.city, country: nextMeta.country });
    }
  }, []);

  const enrichLabel = useCallback(async (baseLoc, source) => {
    const geo = await reverseGeocodeLabel(baseLoc.lat, baseLoc.lon);
    applyLoc(
      { ...baseLoc, city: geo?.city || baseLoc.city, country: geo?.country || baseLoc.country },
      { source, city: geo?.city || baseLoc.city, country: geo?.country || baseLoc.country }
    );
  }, [applyLoc]);

  const detectAuto = useCallback(() => {
    setMessage("");
    setMeta((m) => ({ ...m, loading: true }));
    if (!navigator.geolocation) {
      setMessage("Geolocation not supported.");
      const cached = loadCachedLocation();
      if (cached) {
        enrichLabel({ ...cached, tz_offset_minutes: browserTzOffsetMinutes() }, "auto");
      } else {
        applyLoc(PANCHANGA_FALLBACK, { source: "default", city: PANCHANGA_FALLBACK.city, country: PANCHANGA_FALLBACK.country });
      }
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const base = {
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          tz_offset_minutes: browserTzOffsetMinutes(),
        };
        enrichLabel(base, "auto");
        setMessage("");
      },
      () => {
        setMessage("Location denied — using saved profile or default.");
        const cached = loadCachedLocation();
        if (cached) {
          enrichLabel(cached, "auto");
        } else if (user?.panchanga_prefs?.location) {
          const p = user.panchanga_prefs.location;
          applyLoc(p, { source: "profile", city: p.city, country: p.country });
        } else {
          applyLoc(PANCHANGA_FALLBACK, {
            source: "default",
            city: PANCHANGA_FALLBACK.city,
            country: PANCHANGA_FALLBACK.country,
          });
        }
      },
      { maximumAge: 120000, timeout: 15000, enableHighAccuracy: false }
    );
  }, [applyLoc, enrichLabel, user]);

  const setManualLoc = useCallback(
    (manual) => {
      applyLoc(
        {
          lat: manual.lat,
          lon: manual.lon,
          tz_offset_minutes: manual.tz_offset_minutes,
          city: manual.city,
          country: manual.country,
        },
        { source: "manual", city: manual.city, country: manual.country }
      );
      setMessage("Manual location applied.");
    },
    [applyLoc]
  );

  const saveToProfile = useCallback(
    async () => {
      if (!token || !loc) {
        setMessage("Sign in to save location to your profile.");
        return false;
      }
      try {
        await axios.post(
          `${API}/panchanga/set-location`,
          {
            lat: loc.lat,
            lon: loc.lon,
            tz_offset_minutes: loc.tz_offset_minutes,
            city: loc.city,
            country: loc.country,
            source: meta.source === "manual" ? "manual" : "browser",
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMeta((m) => ({ ...m, source: "profile" }));
        setMessage("Saved to profile.");
        return true;
      } catch {
        setMessage("Could not save profile location.");
        return false;
      }
    },
    [token, loc, meta.source]
  );

  useEffect(() => {
    let cancelled = false;

    function runGeolocation() {
      if (!navigator.geolocation) {
        const cached = loadCachedLocation();
        if (cached) enrichLabel(cached, "auto");
        else
          applyLoc(PANCHANGA_FALLBACK, {
            source: "default",
            city: PANCHANGA_FALLBACK.city,
            country: PANCHANGA_FALLBACK.country,
          });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (cancelled) return;
          enrichLabel(
            {
              lat: pos.coords.latitude,
              lon: pos.coords.longitude,
              tz_offset_minutes: browserTzOffsetMinutes(),
            },
            "auto"
          );
        },
        () => {
          if (cancelled) return;
          const profileLoc = user?.panchanga_prefs?.location;
          if (profileLoc?.lat != null) {
            applyLoc(profileLoc, { source: "profile", city: profileLoc.city, country: profileLoc.country });
            return;
          }
          const cached = loadCachedLocation();
          if (cached) enrichLabel(cached, "auto");
          else
            applyLoc(PANCHANGA_FALLBACK, {
              source: "default",
              city: PANCHANGA_FALLBACK.city,
              country: PANCHANGA_FALLBACK.country,
            });
        },
        { maximumAge: 60000, timeout: 15000, enableHighAccuracy: false }
      );
    }

    async function resolve() {
      setMeta((m) => ({ ...m, loading: true }));

      const fromQuery = parseLocFromSearchParams(searchParams);
      if (fromQuery) {
        if (!cancelled) await enrichLabel(fromQuery, "query");
        return;
      }

      if (token) {
        try {
          const res = await axios.get(`${API}/panchanga/location`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const saved = res.data?.saved;
          if (saved?.lat != null && !cancelled) {
            applyLoc(saved, { source: "profile", city: saved.city, country: saved.country });
            return;
          }
        } catch (_) {}
      }

      if (user?.panchanga_prefs?.location?.lat != null && !cancelled) {
        const p = user.panchanga_prefs.location;
        applyLoc(p, { source: "profile", city: p.city, country: p.country });
        return;
      }

      if (!geoAttempted.current) {
        geoAttempted.current = true;
        runGeolocation();
        return;
      }

      const cached = loadCachedLocation();
      if (cached && !cancelled) {
        enrichLabel(cached, "auto");
        return;
      }

      if (!cancelled) {
        applyLoc(PANCHANGA_FALLBACK, {
          source: "default",
          city: PANCHANGA_FALLBACK.city,
          country: PANCHANGA_FALLBACK.country,
        });
      }
    }

    resolve();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, token, user?.panchanga_prefs?.location]);

  // Re-detect live coordinates when tab regains focus (travel / location change).
  useEffect(() => {
    if (meta.source !== "auto" && meta.source !== "query") return;
    const onVisible = () => {
      if (document.visibilityState !== "visible" || !navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          enrichLabel(
            {
              lat: pos.coords.latitude,
              lon: pos.coords.longitude,
              tz_offset_minutes: browserTzOffsetMinutes(),
            },
            "auto"
          );
        },
        () => {},
        { maximumAge: 0, timeout: 12000, enableHighAccuracy: false }
      );
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [meta.source, enrichLabel]);

  return {
    loc,
    meta,
    loading: meta.loading || !loc,
    message,
    setMessage,
    detectAuto,
    setManualLoc,
    saveToProfile,
  };
}
